/**
 * LoginPage - Payabhut (पायाभूत)
 *
 * Design principles:
 * - Premium, polished startup feel
 * - Strong visual hierarchy
 * - Smooth micro-interactions
 * - Clear brand presence
 * - Accessible and performant
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Phone, Lock, Eye, EyeOff, Loader2, Check } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { getLoginGatha } from "../../config/gathas";
import FunderAcknowledgment from "../../components/common/FunderAcknowledgment";

// Design tokens
const theme = {
  colors: {
    primary: '#4285F4',
    primaryDark: '#3367D6',
    primaryLight: '#E8F0FE',
    background: '#F8FAFC',
    surface: '#FFFFFF',
    surfaceFocus: '#FAFBFC',
    text: {
      primary: '#1F2937',
      secondary: '#6B7280',
      muted: '#9CA3AF',
      inverse: '#FFFFFF',
    },
    border: '#E5E7EB',
    borderFocus: '#4285F4',
    error: '#DC2626',
    errorBg: '#FEF2F2',
    success: '#10B981',
    successBg: '#ECFDF5',
  },
  gradients: {
    // Softer gradient that transitions well to app's #FAFAFA background
    background: 'linear-gradient(180deg, #EDF2F7 0%, #F7FAFC 40%, #FAFAFA 100%)',
    button: 'linear-gradient(135deg, #4285F4 0%, #3367D6 100%)',
    buttonHover: 'linear-gradient(135deg, #3B7DE8 0%, #2D5FC4 100%)',
  },
  shadows: {
    card: '0 2px 8px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.08)',
    cardHover: '0 4px 12px rgba(0,0,0,0.06), 0 12px 40px rgba(0,0,0,0.1)',
    input: 'inset 0 1px 2px rgba(0,0,0,0.06)',
    inputFocus: 'inset 0 1px 2px rgba(0,0,0,0.04), 0 0 0 3px rgba(66, 133, 244, 0.15)',
    button: '0 4px 14px rgba(66, 133, 244, 0.4)',
    buttonHover: '0 6px 20px rgba(66, 133, 244, 0.5)',
  },
};

// CSS Keyframes (injected once)
const injectStyles = () => {
  if (document.getElementById('login-page-styles')) return;

  const style = document.createElement('style');
  style.id = 'login-page-styles';
  style.textContent = `
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(16px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes slideInDown {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      20%, 60% { transform: translateX(-6px); }
      40%, 80% { transform: translateX(6px); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 0.7; }
    }
    @keyframes checkmark {
      0% { stroke-dashoffset: 24; }
      100% { stroke-dashoffset: 0; }
    }
    @keyframes successScale {
      0% { transform: scale(0.8); opacity: 0; }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); opacity: 1; }
    }
    .animate-fadeInUp { animation: fadeInUp 0.6s ease-out forwards; }
    .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
    .animate-scaleIn { animation: scaleIn 0.5s ease-out forwards; }
    .animate-slideInDown { animation: slideInDown 0.3s ease-out forwards; }
    .animate-shake { animation: shake 0.4s ease-out; }
    .animate-pulse-subtle { animation: pulse 2s ease-in-out infinite; }
    .animate-success { animation: successScale 0.4s ease-out forwards; }
    .stagger-1 { animation-delay: 0.1s; opacity: 0; }
    .stagger-2 { animation-delay: 0.2s; opacity: 0; }
    .stagger-3 { animation-delay: 0.3s; opacity: 0; }
    .stagger-4 { animation-delay: 0.4s; opacity: 0; }
  `;
  document.head.appendChild(style);
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();

  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [shakeError, setShakeError] = useState(false);

  const [gathaData] = useState(() => getLoginGatha());
  const errorTimeoutRef = useRef(null);
  const formRef = useRef(null);

  // Inject animation styles
  useEffect(() => {
    injectStyles();
  }, []);

  // Mount animation
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Redirect if authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const returnTo = location.state?.from?.pathname || "/teacher";
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      errorTimeoutRef.current = setTimeout(() => {
        setError("");
      }, 5000);
    }
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, [error]);

  const isFormValid = phone.trim().length >= 10 && password.length >= 1;

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!isFormValid || isSubmitting) return;

    setError("");
    setIsSubmitting(true);

    try {
      await login(phone.trim(), password);
      setLoginSuccess(true);

      // Brief success state before redirect
      setTimeout(() => {
        const returnTo = location.state?.from?.pathname || "/teacher";
        navigate(returnTo);
      }, 800);
    } catch (err) {
      setError(err.message || "We couldn't sign you in. Please check your phone number and password.");
      setShakeError(true);
      setTimeout(() => setShakeError(false), 400);
      setIsSubmitting(false);
    }
  }, [isFormValid, isSubmitting, login, phone, password, location, navigate]);

  const dismissError = () => {
    setError("");
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: theme.gradients.background }}
    >
      {/* Main Content */}
      <main className="flex-1 flex flex-col justify-center px-5 py-6 md:py-8">
        <div className={`w-full max-w-[420px] mx-auto ${mounted ? '' : 'opacity-0'}`}>

          {/* Header Section */}
          <header className={`text-center mb-8 md:mb-10 ${mounted ? 'animate-fadeInUp' : ''}`}>
            {/* Logo with scale animation */}
            <div className="mb-5 stagger-1 animate-scaleIn">
              <img
                src="/PadhoJalnaLogo.png"
                alt="Padho Jalna"
                className="w-[90px] md:w-[110px] lg:w-[120px] h-auto mx-auto"
                draggable={false}
                loading="eager"
                style={{
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.1))',
                  willChange: 'transform, opacity',
                }}
              />
            </div>

            {/* App Name with strong typography */}
            <h1
              className="text-[32px] md:text-[40px] lg:text-[44px] font-extrabold tracking-wide stagger-2 animate-fadeInUp"
              style={{
                color: theme.colors.primary,
                letterSpacing: '0.02em',
              }}
            >
              पायाभूत
            </h1>

            {/* Tagline */}
            <p
              className="mt-2 text-[14px] md:text-[15px] stagger-3 animate-fadeInUp"
              style={{ color: theme.colors.text.secondary }}
            >
              Building Foundations for Every Child
            </p>
          </header>

          {/* Login Card */}
          <div
            ref={formRef}
            className={`rounded-3xl p-8 md:p-10 stagger-4 animate-fadeInUp ${shakeError ? 'animate-shake' : ''}`}
            style={{
              backgroundColor: theme.colors.surface,
              boxShadow: theme.shadows.card,
              willChange: 'transform',
            }}
          >
            {/* Success State */}
            {loginSuccess ? (
              <div className="py-8 text-center animate-success">
                <div
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: theme.colors.successBg }}
                >
                  <Check
                    size={32}
                    strokeWidth={3}
                    style={{ color: theme.colors.success }}
                  />
                </div>
                <p
                  className="text-lg font-semibold"
                  style={{ color: theme.colors.text.primary }}
                >
                  Welcome back!
                </p>
                <p
                  className="text-sm mt-1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Signing you in...
                </p>
              </div>
            ) : (
              <>
                {/* Error Alert */}
                {error && (
                  <div
                    className="mb-6 px-4 py-3 rounded-2xl flex items-start gap-3 animate-slideInDown"
                    style={{ backgroundColor: theme.colors.errorBg }}
                    role="alert"
                  >
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill={theme.colors.error} viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm flex-1" style={{ color: theme.colors.error }}>
                      {error}
                    </p>
                    <button
                      onClick={dismissError}
                      className="p-0.5 rounded hover:bg-red-100 transition-colors"
                      aria-label="Dismiss error"
                    >
                      <svg className="w-4 h-4" fill={theme.colors.error} viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Phone Field */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-semibold mb-2.5"
                      style={{ color: theme.colors.text.primary }}
                    >
                      Phone Number
                    </label>
                    <div className="relative">
                      <span
                        className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-200"
                        style={{ color: theme.colors.text.muted }}
                      >
                        <Phone size={18} strokeWidth={1.75} />
                      </span>
                      <input
                        id="phone"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                          if (error) setError("");
                        }}
                        placeholder="10-digit mobile number"
                        className="
                          w-full h-[56px] pl-12 pr-4
                          text-base rounded-2xl
                          border-2 outline-none
                          transition-all duration-200
                          placeholder:text-gray-400
                          focus:border-[#4285F4]
                        "
                        style={{
                          backgroundColor: theme.colors.surface,
                          borderColor: error ? theme.colors.error : theme.colors.border,
                          color: theme.colors.text.primary,
                          boxShadow: theme.shadows.input,
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = theme.colors.surfaceFocus;
                          e.target.style.boxShadow = theme.shadows.inputFocus;
                          e.target.style.borderColor = theme.colors.borderFocus;
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = theme.colors.surface;
                          e.target.style.boxShadow = theme.shadows.input;
                          e.target.style.borderColor = error ? theme.colors.error : theme.colors.border;
                        }}
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-semibold mb-2.5"
                      style={{ color: theme.colors.text.primary }}
                    >
                      Password
                    </label>
                    <div className="relative">
                      <span
                        className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-200"
                        style={{ color: theme.colors.text.muted }}
                      >
                        <Lock size={18} strokeWidth={1.75} />
                      </span>
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (error) setError("");
                        }}
                        placeholder="Enter password"
                        className="
                          w-full h-[56px] pl-12 pr-14
                          text-base rounded-2xl
                          border-2 outline-none
                          transition-all duration-200
                          placeholder:text-gray-400
                          focus:border-[#4285F4]
                        "
                        style={{
                          backgroundColor: theme.colors.surface,
                          borderColor: error ? theme.colors.error : theme.colors.border,
                          color: theme.colors.text.primary,
                          boxShadow: theme.shadows.input,
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = theme.colors.surfaceFocus;
                          e.target.style.boxShadow = theme.shadows.inputFocus;
                          e.target.style.borderColor = theme.colors.borderFocus;
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = theme.colors.surface;
                          e.target.style.boxShadow = theme.shadows.input;
                          e.target.style.borderColor = error ? theme.colors.error : theme.colors.border;
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors"
                        style={{ color: theme.colors.text.muted }}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={!isFormValid || isSubmitting}
                    className={`
                      w-full h-[56px] rounded-2xl
                      text-base font-semibold
                      transition-all duration-200
                      flex items-center justify-center gap-2
                      ${!isFormValid && !isSubmitting ? 'animate-pulse-subtle' : ''}
                      ${isFormValid && !isSubmitting ? 'hover:shadow-xl active:scale-[0.98]' : ''}
                    `}
                    style={{
                      background: isFormValid ? theme.gradients.button : '#D1D5DB',
                      color: theme.colors.text.inverse,
                      boxShadow: isFormValid && !isSubmitting ? theme.shadows.button : 'none',
                      cursor: !isFormValid || isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: !isFormValid ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (isFormValid && !isSubmitting) {
                        e.target.style.background = theme.gradients.buttonHover;
                        e.target.style.boxShadow = theme.shadows.buttonHover;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isFormValid && !isSubmitting) {
                        e.target.style.background = theme.gradients.button;
                        e.target.style.boxShadow = theme.shadows.button;
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        <span>Signing in...</span>
                      </>
                    ) : (
                      <span>Sign In</span>
                    )}
                  </button>
                </form>

                {/* Help Link */}
                <p className="mt-6 text-center">
                  <a
                    href="tel:+919876543210"
                    className="text-sm font-medium transition-all duration-200 hover:opacity-80"
                    style={{ color: theme.colors.primary }}
                  >
                    Need help signing in?
                  </a>
                </p>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer
        className={`py-6 px-6 ${mounted ? 'animate-fadeIn' : 'opacity-0'}`}
        style={{
          borderTop: `1px solid ${theme.colors.border}`,
          animationDelay: '0.5s',
        }}
      >
        <div className="flex flex-col items-center gap-3">
          <p
            className="text-xs italic leading-relaxed max-w-xs text-center"
            style={{ color: theme.colors.text.muted, opacity: 0.7 }}
          >
            "{gathaData.text}"{gathaData.source && <span className="not-italic" style={{ opacity: 0.5 }}> — {gathaData.source}</span>}
          </p>
          <img
            src="/logos/jef logo.png"
            alt="Jalna Education Foundation"
            className="h-11 md:h-12 w-auto"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <p
            className="text-xs"
            style={{ color: theme.colors.text.muted }}
          >
            Jalna Education Foundation
          </p>
          <FunderAcknowledgment as="div" />
        </div>
      </footer>
    </div>
  );
}
